import {
  CheckCircleIcon,
  WarningCircleIcon,
  CaretRightIcon,
  TruckIcon,
  ReceiptIcon,
  DownloadSimpleIcon,
  MapPinIcon,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { useAllGeneralSettings } from '../../all-settings-context';
import { Pill, type PillColor } from '../../../../components/pill/Pill';
import { useTelemetryConnections } from '../../../telemetry/models';

interface IIntegrationCard {
  slug: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  statusLabel: string;
  statusColor: PillColor;
}

export const IntegrationsOverview = () => {
  const { settings } = useAllGeneralSettings();
  const { data: telemetryConnections } = useTelemetryConnections();
  const activeTelemetryConnections = telemetryConnections?.filter((c) => c.status === 'Active').length ?? 0;

  const integrations: IIntegrationCard[] = [
    {
      slug: 'teleroute',
      name: 'Teleroute',
      description: 'Plaats orders als vrachtaanbod op de Teleroute spotmarkt.',
      icon: <TruckIcon className="w-6 h-6" />,
      statusLabel: settings.telerouteIsConnected
        ? settings.telerouteEnabled
          ? 'Actief'
          : 'Gekoppeld (inactief)'
        : 'Niet gekoppeld',
      statusColor: settings.telerouteIsConnected && settings.telerouteEnabled ? 'positive' : 'primary',
    },
    {
      slug: 'transporeon',
      name: 'Transporeon',
      description: 'Importeer orders automatisch vanuit Transporeon.',
      icon: <DownloadSimpleIcon className="w-6 h-6" />,
      statusLabel: settings.transporeonImportEnabled ? 'Actief' : 'Inactief',
      statusColor: settings.transporeonImportEnabled ? 'positive' : 'primary',
    },
    {
      slug: 'billit',
      name: 'Billit',
      description: 'Synchroniseer facturen met het Billit boekhoudplatform.',
      icon: <ReceiptIcon className="w-6 h-6" />,
      statusLabel: settings.billitPartyId ? 'Geconfigureerd' : 'Niet geconfigureerd',
      statusColor: settings.billitPartyId ? 'positive' : 'primary',
    },
    {
      slug: 'tracking',
      name: 'Tracking',
      description: 'Volg de locatie en boordcomputer van trekkers via een trackingprovider.',
      icon: <MapPinIcon className="w-6 h-6" />,
      statusLabel:
        activeTelemetryConnections > 0
          ? `${activeTelemetryConnections} actief`
          : telemetryConnections && telemetryConnections.length > 0
            ? 'Fout'
            : 'Niet gekoppeld',
      statusColor: activeTelemetryConnections > 0 ? 'positive' : 'primary',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {integrations.map((integration) => (
        <Link
          key={integration.slug}
          to={integration.slug}
          className="card p-4 flex flex-col gap-3 hover:border-border-strong transition-colors"
          data-clickable="true"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="text-fg-muted">{integration.icon}</div>
              <div className="card-heading-text">{integration.name}</div>
            </div>
            <div className="flex items-center gap-1">
              {integration.statusColor === 'positive' ? (
                <CheckCircleIcon className="w-4 h-4 text-positive-text" weight="fill" />
              ) : (
                <WarningCircleIcon className="w-4 h-4 text-warning-text" weight="fill" />
              )}
              <Pill color={integration.statusColor} size="sm">
                {integration.statusLabel}
              </Pill>
            </div>
          </div>

          <div className="text-sm text-fg-muted">{integration.description}</div>

          <div className="flex justify-end text-sm text-brand-text items-center gap-1">
            Configureren
            <CaretRightIcon className="w-4 h-4" />
          </div>
        </Link>
      ))}
    </div>
  );
};
